import { useMemo } from 'react'
import useQueryParams from 'hooks/useQueryParams'
import { LUXURY_ESCAPES } from 'constants/brands'
import config from 'constants/config'
import { useAppSelector } from 'hooks/reduxHooks'
import { getCanRedeemLuxPlusBenefits, getIsEnabledLuxPlusDiscountedCruise } from 'luxPlus/selectors/featureToggle'

export interface CruiseFacetFiltersParams {
  queryParamsFilters: CruiseQueryParamsFilters
  facetParams: App.CruiseSearchFacetParams
  mappedFilters: CruiseQueryParamsFiltersMapped
}

interface CruiseQueryParamsFilters {
  price: {
    min?: number
    max?: number
  }
  cabinTypes?: Set<string>
  cruiseShips?: Set<string>
  promotions?: Set<string>
  isSpecialOffers?: Set<string>
  isLuxExclusivePromotion?: Set<string>
  isLuxPlus?: Set<string>
  hasLuxPlusPrice?: Set<string>
  isCruiseTour?: boolean
  campaigns?: Set<string>
  cruiseTypes?: Set<string>
  travellerTypes?: Set<string>
  durationRange?: Set<string>
}

interface CruiseQueryParamsFiltersMapped {
  cruiseShips: Array<string>
  promotions: Array<string>
  isLuxPlus?: boolean
  isLuxPlusMember?: boolean
  hasLuxPlusPrice?: boolean
  isLuxExclusivePromotion?: boolean
  isSpecialOffers?: boolean
  isCruiseTour?: boolean
  cabinTypes?: Array<string>
  minPrice?: number
  maxPrice?: number
  campaigns?: Array<string>
  cruiseTypes?: Array<string>
  travellerTypes?: Array<string>
  durationRange?: Array<string>
  offerDepartureIds?: Array<string>
  price: {
    min?: number
    max?: number
  }
}

const isLuxuryEscapes = config.BRAND === LUXURY_ESCAPES
interface FiltersProps {
  filters: App.OfferListFilters
}

export default function useCruiseFacetFiltersParams({ filters }: FiltersProps): CruiseFacetFiltersParams {
  const queryParams = useQueryParams()
  const isLuxPlusMember = useAppSelector(state =>
    getCanRedeemLuxPlusBenefits(state) && getIsEnabledLuxPlusDiscountedCruise(state),
  )

  const queryParamsFilters = useMemo<CruiseQueryParamsFilters>(() => {
    const luxExclusiveValue = queryParams.get('isLuxExclusivePromotion')
    const isLuxPlus = queryParams.get('isLuxPlus')
    const hasLuxPlusPriceParam = queryParams.get('hasLuxPlusPrice')
    const isCruiseTour = queryParams.get('isCruiseTour')
    const priceMin = queryParams.get('minPrice')
    const priceMax = queryParams.get('maxPrice')
    const cabinTypes = new Set(queryParams.getAll('cabinTypes'))
    const campaigns = queryParams.getAll('campaigns')
    const cruiseTypes = queryParams.getAll('cruiseTypes')
    const travellerTypes = queryParams.getAll('travellerTypes')
    const durationRange = queryParams.getAll('durationRange')

    return {
      price: {
        min: priceMin ? parseInt(priceMin) : undefined,
        max: priceMax ? parseInt(priceMax) : undefined,
      },
      cabinTypes,
      cruiseShips: new Set(queryParams.getAll('cruiseShips')),
      promotions: new Set(queryParams.getAll('promotions')),
      isSpecialOffers: queryParams.get('isSpecialOffers') ? new Set(['special_offers']) : undefined,
      isLuxExclusivePromotion: luxExclusiveValue && isLuxuryEscapes ? new Set(['lux_exclusive']) : undefined,
      isLuxPlus: isLuxPlus && isLuxuryEscapes ? new Set(['lux_plus']) : undefined,
      hasLuxPlusPrice: hasLuxPlusPriceParam && isLuxuryEscapes ? new Set(['member_prices']) : undefined,
      isCruiseTour: Boolean(isCruiseTour) || undefined,
      campaigns: new Set(campaigns),
      cruiseTypes: new Set(cruiseTypes),
      travellerTypes: new Set(travellerTypes),
      durationRange: durationRange.length ? new Set(durationRange) : undefined,
    }
  }, [queryParams])

  const mappedFilters = useMemo<CruiseQueryParamsFiltersMapped>(() => {
    const isLuxPlus = queryParamsFilters?.isLuxPlus?.size! > 0 || undefined
    const isLuxExclusivePromotion = queryParamsFilters?.isLuxExclusivePromotion?.size! > 0 || undefined
    const isSpecialOffers = queryParamsFilters?.isSpecialOffers?.size! > 0 || undefined
    const hasLuxPlusPrice = queryParamsFilters?.hasLuxPlusPrice?.size! > 0 || undefined
    const isCruiseTour = queryParamsFilters?.isCruiseTour || undefined

    return {
      ...queryParamsFilters,
      cabinTypes: Array.from(queryParamsFilters?.cabinTypes || []),
      minPrice: queryParamsFilters?.price?.min,
      maxPrice: queryParamsFilters?.price?.max,
      isSpecialOffers,
      isLuxExclusivePromotion,
      isLuxPlus,
      isLuxPlusMember: isLuxPlusMember || undefined,
      hasLuxPlusPrice,
      isCruiseTour,
      cruiseShips: Array.from(queryParamsFilters?.cruiseShips || []),
      promotions: Array.from(queryParamsFilters?.promotions || []),
      campaigns: Array.from(queryParamsFilters?.campaigns || []),
      cruiseTypes: Array.from(queryParamsFilters?.cruiseTypes || []),
      travellerTypes: Array.from(queryParamsFilters?.travellerTypes || []),
      durationRange: queryParamsFilters?.durationRange ? Array.from(queryParamsFilters.durationRange) : undefined,
    }
  }, [queryParamsFilters, isLuxPlusMember])

  const facetParams = useMemo<App.CruiseSearchFacetParams>(() => ({
    facetTypes: ['ship_names', 'cabin_types', 'special_offers', 'lux_exclusive', 'cruise_prices', 'lux_plus', 'campaigns', 'cruise_types', 'traveller_types', 'lux_plus_price', 'cruise_durations'],
    cruiseLines: filters.cruiseLines,
    departurePlaceId: filters.departurePlaceId,
    departureIds: filters.departureIds,
    offerDepartureIds: filters.offerDepartureIds,
    rateCodes: filters.rateCodes,
    destinationId: filters.destinationId,
    destinationIds: filters.destinationIds,
    departureMonths: filters.departureMonths,
    durationMin: filters.durationMin,
    durationMax: filters.durationMax,
    departureStartDate: filters.departureStartDate,
    departureEndDate: filters.departureEndDate,
    cruiseTypes: filters.cruiseTypes,
    travellerTypes: filters.travellerTypes,
    isCruiseTour: filters.isCruiseTour,
    ...mappedFilters,
    durationRange: mappedFilters.durationRange ?? filters.durationRange,
    offerSource: 'cruises',
  }), [
    filters.cruiseLines,
    filters.departurePlaceId,
    filters.departureIds,
    filters.offerDepartureIds,
    filters.rateCodes,
    filters.destinationId,
    filters.destinationIds,
    filters.departureMonths,
    filters.durationMin,
    filters.durationMax,
    filters.departureStartDate,
    filters.departureEndDate,
    filters.durationRange,
    filters.cruiseTypes,
    filters.travellerTypes,
    filters.isCruiseTour,
    mappedFilters,
  ])

  return {
    queryParamsFilters,
    facetParams,
    mappedFilters,
  }
}
